/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {Schema} from 'prosemirror-model';
import {EditorState, Plugin, Transaction} from 'prosemirror-state';
import {EditorProps, EditorView} from 'prosemirror-view';
import {Observable, Subject} from 'rxjs';
import {isNil} from '../../utils';
import EditorCommands from './EditorCommands';
import defautlSchema from './schema';
import {parseContent} from './parsers';
import getDefaultPlugins from './defaultPlugins';

type JSONDoc = Record<string, any>;
type Content = string | null | JSONDoc;

interface Options {
    content?: Content;
    history?: boolean;
    keyboardShortcuts?: boolean;
    inputRules?: boolean;
    schema?: Schema;
    plugins?: Plugin[];
    nodeViews?: EditorProps['nodeViews'];
    attributes?: Record<string, string>;
    features?: EditorFeatures,
    isValidImageFile: (file: any) => boolean
}

interface EditorFeatures {
    linkOnPaste?: boolean;
    resizeImage?: boolean;
}

const defaultFeatures = {
    linkOnPaste: true,
    resizeImage: true
};

const DEFAULT_OPTIONS: Options = {
    content: null,
    history: true,
    keyboardShortcuts: true,
    inputRules: true,
    schema: defautlSchema,
    plugins: [],
    nodeViews: {},
    attributes: {},
    features: defaultFeatures,
    isValidImageFile: ({}) => true
};

class Editor {
    /**
     * Editor options.
     */
    options: Options;

    /**
     * View instance.
     */
    view: EditorView;

    /**
     * Default constructor.
     * 
     * @param options Editor Options.
     */
    constructor(options: Options = DEFAULT_OPTIONS) {
        this.options = Object.assign({}, DEFAULT_OPTIONS, options);
        this.createEditor();
    }

    private valueChangesSubject = new Subject<JSONDoc>();

    private updateSubject = new Subject<EditorView>();

    /**
     * Getter for value changes.
     * 
     * @returns Observable.
     */
    get valueChanges(): Observable<JSONDoc> {
        return this.valueChangesSubject.asObservable();
    }

    /**
     * Getter for update.
     * 
     * @returns Observable.
     */
    get update(): Observable<EditorView> {
        return this.updateSubject.asObservable();
    }

    /**
     * Getter for schema.
     * 
     * @returns Schema.
     */
    get schema(): Schema {
        return this.options.schema || defautlSchema;
    }

    /**
     * Getter for commands.
     * 
     * @returns EditorCommands.
     */
    get commands(): EditorCommands {
        return new EditorCommands(this.view);
    }

    /**
     * Getter for features.
     * 
     * @returns EditorFeatures.
     */
    get features(): EditorFeatures {
        return {...defaultFeatures, ...this.options.features};
    }

    private handleTransactions(tr: Transaction): void {
        const state = this.view.state.apply(tr);
        this.view.updateState(state);

        this.updateSubject.next(this.view);

        if (!tr.docChanged && !tr.getMeta('FORCE_EMIT')) {
            return;
        }

        const json = state.doc.toJSON();
        this.valueChangesSubject.next(json);
    }

    private createEditor(): void {
        const {options} = this;
        const {content = null, nodeViews} = options;
        const {history = true, keyboardShortcuts = true, inputRules = true} = options;
        const schema = this.schema;

        const doc = parseContent(content, schema);

        const plugins: Plugin[] = options.plugins ?? [];
        const attributes: Record<string, string> = options.attributes ?? {};

        const defaultPlugins = getDefaultPlugins(schema, {
            history,
            keyboardShortcuts,
            inputRules
        });

        this.view = new EditorView(undefined, {
            state: EditorState.create({
                doc,
                schema,
                plugins: [...defaultPlugins, ...plugins]
            }),
            nodeViews,
            dispatchTransaction: this.handleTransactions.bind(this),
            attributes
        });
    }

    /**
     * Setter for content.
     * 
     * @param content Content.
     */
    setContent(content: Content): void {
        if (isNil(content)) {
            return;
        }

        const {state} = this.view;
        const {tr, doc} = state;

        const newDoc = parseContent(content, this.schema);

        tr.replaceWith(0, state.doc.content.size, newDoc);

        // don't emit if both content is same
        if (doc.eq(tr.doc)) {
            return;
        }

        if (!tr.docChanged) {
            return;
        }

        this.view.dispatch(tr);
    }

    /**
     * Register plugin.
     * 
     * @param plugin Plugin.
     */
    registerPlugin(plugin: Plugin): void {
        const {state} = this.view;
        const plugins = [...state.plugins, plugin];

        const newState = state.reconfigure({plugins});
        this.view.updateState(newState);
    }

    /**
     * Destroy function.
     */
    destroy(): void {
        this.view.destroy();
    }
}

export default Editor;
